import { usePackageItemGroup } from "@app-contexts/dashboard/features/box-office/basket/hooks/usePackageItemGroup";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import { ShippingMethodsTypeMapIdType } from "@shared/constants/enums/enums-types";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { useBasketItemGroup } from "@shared/modules/basket/services/useBasketItemGroup";
import { getIsAutoDiscount } from "@shared/modules/basket/utils/discount-utils";
import {
  TicketItemGroupPermissionType,
  TicketItemGroupType,
} from "@shared/modules/item-groups/types/ticket-item-group-type";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SEATING_ITEM_TYPE_MAP } from "@shared/modules/seating/types/seating-property-maps";
import { SeatingEventPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { useCallback, useMemo } from "react";

/**
 * this hook implements the logic ItemGroups in the Review Page of the checkout
 *
 */
export default function useReviewItemGroup(itemGroup: TicketItemGroupType) {
  const {
    isOpen: isRemoveModalOpen,
    onOpen: onOpenRemoveModal,
    onClose: onCloseRemoveModal,
  } = useDisclosure();
  const basketService = usePublicBasket();
  const {
    onRemove,
    shouldDisplay,
    onUpdateItemGroupQuantity,
    onUpdateItemGroupShippingType,
  } = useBasketItemGroup(itemGroup, basketService);
  const { subItems, subItemsQuantity } = usePackageItemGroup(
    itemGroup,
    basketService
  );
  const { isBasketUpdating, appliedDiscounts, hasEntireOrderDiscount } =
    basketService;

  const priceAdjustments = useMemo(() => {
    const isAutoDiscountApplied = getIsAutoDiscount(appliedDiscounts.discount);

    const discountText = isAutoDiscountApplied
      ? appliedDiscounts.discount?.description
      : itemGroup.discount_code;
    const hasCredit =
      itemGroup.credit_applied !== undefined && itemGroup.credit_applied > 0;

    const hasDiscountToShow =
      Boolean(discountText) ||
      (itemGroup.discounts_applied && itemGroup.discounts_applied.length > 0);

    // only show new item price if there are discounts or credits applied to item group
    const showNewItemPrice =
      itemGroup.subtotal !== itemGroup.ig_subtotal &&
      (hasCredit || hasDiscountToShow);

    return {
      isAutoDiscountApplied,
      showNewItemPrice,
      hasCredit,
      discountText,
      hasDiscountToShow,
    };
  }, [
    appliedDiscounts.discount,
    itemGroup.subtotal,
    itemGroup.ig_subtotal,
    itemGroup.credit_applied,
    itemGroup.discount_code,
    itemGroup.discounts_applied,
  ]);

  const handleRemove = () => {
    onRemove().then(() => {
      onCloseRemoveModal();
      UserInteractionLogger.addToCart({
        item: itemGroup,
        count: 0,
        venue: basketService.paymentVenue,
        basket: basketService,
      });
    });
  };

  const handleChangeQuantity = (quantity: number) => {
    onUpdateItemGroupQuantity(quantity);
    UserInteractionLogger.addToCart({
      item: itemGroup,
      count: quantity,
      venue: basketService.paymentVenue,
      basket: basketService,
    });
  };

  const handleChangeShippingType = (
    shippingType: ShippingMethodsTypeMapIdType
  ) => {
    onUpdateItemGroupShippingType(shippingType);
  };

  const seatingItemGroup = useReviewSeatingItem(itemGroup);

  return {
    itemGroupActions: {
      onOpenRemoveModal,
      onCloseRemoveModal,
      handleRemove,
      handleChangeQuantity,
      handleChangeShippingType,
    },
    itemGroupDisplay: {
      shouldDisplay,
      isBasketUpdating,
      subItems,
      subItemsQuantity,
      appliedDiscounts,
      hasEntireOrderDiscount,
      isRemoveModalOpen,
      priceAdjustments,
    },
    seatingItemGroupActions: seatingItemGroup.actions,
    seatingItemGroupDisplay: seatingItemGroup.display,
  };
}

function useReviewSeatingItem(itemGroup: TicketItemGroupType) {
  const {
    actions: seatingActions,
    seatingServiceState,
    modalValues,
  } = useSeatingState();
  const {
    isOpen: isRemoveSeatModalOpen,
    onOpen: onOpenRemoveSeatModal,
    onClose: onCloseRemoveSeatModal,
  } = useDisclosure();

  const {
    modalOpen: removeSeatModalOpen,
    resolveModal,
    rejectModal,
    removeAllSeatsData,
  } = modalValues;

  const { useRetrieveWithMembershipTicketTypesOverride: useRetrieveEvent } =
    usePublicEventService();
  const { useRetrieve: useRetrieveMembership } =
    usePublicMembershipGroupsService();

  const { data: eventData } = useRetrieveEvent(itemGroup.event, {
    options: {
      enabled: Boolean(itemGroup.event) && Boolean(itemGroup?.venue_id),
    },
  });

  const { data: groupData } = useRetrieveMembership(
    itemGroup.membership_group,
    {
      options: {
        enabled:
          Boolean(itemGroup.membership_group) && Boolean(itemGroup?.venue_id),
      },
    }
  );

  // TODO: update this logic when multi-map memberships are implemented
  const getSeatingFormattedEvent = useCallback((): SeatingEventPropertyMap => {
    if (itemGroup.membership_group && groupData) {
      return {
        ...seatingServiceState.createNewSeatingObject(
          groupData,
          SEATING_ITEM_TYPE_MAP.MEMBERSHIP.propertyMap
        ),
        no_isolated_seats:
          groupData.reserved_seating_benefits?.[0].no_isolated_seats,
        assigned_space: groupData.reserved_seating_benefits?.[0].space,
      };
    }
    if (itemGroup.event && eventData) {
      return seatingServiceState.createNewSeatingObject(
        eventData,
        SEATING_ITEM_TYPE_MAP.EVENT.propertyMap
      );
    }
    return {};
  }, [itemGroup, eventData, groupData, seatingServiceState]);

  const handleRemoveAssignedSeat = async (
    permission: TicketItemGroupPermissionType
  ) => {
    const formattedEvent = getSeatingFormattedEvent();

    try {
      if (permission.seat) {
        await seatingActions.initRemoveSeatPermissions(
          permission,
          itemGroup,
          formattedEvent
        );
      }
      if (permission.location) {
        await seatingActions.initModifyLocationPermissions(
          permission,
          itemGroup,
          0
        );
      }
    } catch (error) {
      // initRemoveSeatPermissions shows toast on error already
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleResolveSeatingModal = (val: unknown) => {
    resolveModal(val);
    onCloseRemoveSeatModal();
  };

  const handleCloseSeatingModal = (val: unknown) => {
    rejectModal(val);
    onCloseRemoveSeatModal();
  };

  return {
    actions: {
      handleRemoveAssignedSeat,
      handleResolveSeatingModal,
      onOpenRemoveSeatModal,
      handleCloseSeatingModal,
    },
    display: {
      removeSeatModalOpen,
      removeAllSeatsData,
      isRemoveSeatModalOpen,
    },
  };
}
